/** Company skills, person memberships and type requirements share the same query. */
import { del, get, post, put } from '@/api/client';
import { useCan } from '@/auth/caps';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateShifts } from './shifts';

export interface ApiSkill {
  id: number;
  name: string;
  people: number[];
  shift_types: number[];
  /** Fingerprint of the definition and relationships actually read. */
  revision: string;
}
export type SkillWrite = {
  name?: string;
  shift_types?: number[];
  expected_revision: string;
};

export function useSkills() {
  const can = useCan();
  return useQuery({
    queryKey: ['skills'],
    queryFn: () => get<ApiSkill[]>('/admin/skills'),
    enabled: can('vaktplan.se') || can('innstillinger.endre') || can('ansatte.endre'),
    staleTime: 60_000,
  });
}

export function useSkillWrites() {
  const qc = useQueryClient();
  const done = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: ['skills'] }), invalidateShifts(qc)]);
  };
  const accept = (skill: ApiSkill) =>
    qc.setQueryData<ApiSkill[]>(['skills'], (old) =>
      old
        ? [...old.filter((k) => k.id !== skill.id), skill].sort((a, b) => a.name.localeCompare(b.name, 'nb'))
        : [skill],
    );
  return {
    create: useMutation({
      mutationFn: (body: { name: string }) => post<ApiSkill>('/admin/skills', body),
      onSuccess: accept,
      onSettled: done,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: number; body: SkillWrite }) =>
        put<ApiSkill>(`/admin/skills/${id}`, body),
      onSuccess: accept,
      onSettled: done,
    }),
    remove: useMutation({
      mutationFn: ({ id, revision }: Pick<ApiSkill, 'id' | 'revision'>) =>
        del<{ id: number }>(`/admin/skills/${id}?revision=${encodeURIComponent(revision)}`),
      onSettled: done,
    }),
    setPerson: useMutation({
      mutationFn: ({
        userId,
        skills,
        expected_skills,
      }: { userId: number; skills: number[]; expected_skills: number[] }) =>
        put<{ user_id: number; skills: number[] }>(`/admin/users/${userId}/skills`, {
          skills,
          expected_skills,
        }),
      onSettled: done,
    }),
  };
}
